import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Checkbox, Form, Input, Modal, Space, Tag, Tooltip } from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type { LanguageRow, ListResponse, NotificationsResponse } from "../api/types";
import { DataTable } from "../components/DataTable";

interface LanguageFormValues {
  language_code: string;
  denomination: string;
  system: boolean;
}

/**
 * Sidebar entry "language_management" → /webmaster/languages.
 *
 * The languages talks and circumscriptions are held in. `system` marks the ones
 * that can also be picked as an interface language — the application itself is
 * translated into those, so it is not a property a new entry inherits.
 *
 * Disabling is refused while a user, a circumscription or a speech still points
 * at the language; the server answers 200 and names the dependency in a
 * notification. There is no endpoint that switches a language back on, which is
 * exactly why that refusal has to hold.
 */
export function LanguageManagementPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<LanguageFormValues>();

  const [editing, setEditing] = useState<LanguageRow | "new" | null>(null);

  const languages = useQuery({
    queryKey: ["webmaster", "languages"],
    queryFn: () => apiFetch<ListResponse<LanguageRow>>("/webmaster/languages"),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(() => {
    // The other webmaster screens read this list for their language selects.
    void queryClient.invalidateQueries({ queryKey: ["webmaster", "languages"] });
  }, [queryClient]);

  const save = useMutation({
    mutationFn: (values: LanguageFormValues) =>
      editing === "new" || editing === null
        ? apiFetch<NotificationsResponse>("/webmaster/languages", {
            method: "POST",
            body: values,
          })
        : apiFetch<NotificationsResponse>(`/webmaster/languages/${editing.id}`, {
            method: "PUT",
            body: values,
          }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refresh();
    },
    onError: showError,
  });

  const disable = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/webmaster/languages/${id}`, { method: "DELETE" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const openEditor = useCallback(
    (row: LanguageRow | "new") => {
      setEditing(row);
      // resetFields before filling. The store no longer drops its values when
      // the dialog closes, so a field this handler does not write would keep
      // the previously edited row's value.
      form.resetFields();
      if (row !== "new") {
        form.setFieldsValue({
          language_code: row.language_code ?? "",
          denomination: row.denomination ?? "",
          system: row.system,
        });
      }
    },
    [form],
  );

  const askAndDisable = useCallback(
    (row: LanguageRow) => {
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: t("dialog.confirm_deactivate", {
          name: [row.language_code, row.denomination].filter(Boolean).join(" – "),
        }),
        okText: t("dialog.answer_yes"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.answer_no"),
        onOk: () => disable.mutate(row.id),
      });
    },
    [modal, t, disable],
  );

  const busy = save.isPending || disable.isPending;

  const columns = useMemo<TableProps<LanguageRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.language.language_code"),
        dataIndex: "language_code",
        width: 100,
        sorter: (a, b) => compareText(a.language_code, b.language_code),
      },
      {
        title: t("activerecord.attributes.language.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.denomination, b.denomination),
      },
      {
        title: t("activerecord.attributes.language.system"),
        dataIndex: "system",
        width: 140,
        render: (value: boolean) => (value ? <Tag color="blue">{t("yes")}</Tag> : null),
      },
      {
        key: "actions",
        fixed: "right",
        width: 100,
        render: (_value, row) => (
          <Space size={4}>
            <Tooltip title={t("form.update")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={busy}
                onClick={() => openEditor(row)}
              />
            </Tooltip>
            <Tooltip title={t("language.disable")}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={busy}
                onClick={() => askAndDisable(row)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [t, busy, openEditor, askAndDisable],
  );

  return (
    <Card
      title={t("navigation.language_management")}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor("new")}>
          {t("language.create")}
        </Button>
      }
    >
      <DataTable<LanguageRow>
        columns={columns}
        rows={languages.data?.data}
        exportAs={t("navigation.language_management")}
        loading={languages.isPending || busy}
        error={languages.error}
      />

      <Modal
        open={editing !== null}
        title={editing === "new" ? t("language.create") : editing?.denomination}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<LanguageFormValues>
          form={form}
          layout="vertical"
          initialValues={{ system: false }}
          onFinish={(values) => save.mutate(values)}
        >
          <Form.Item
            name="language_code"
            label={t("activerecord.attributes.language.language_code")}
            rules={[{ required: true }]}
          >
            <Input style={{ width: 120 }} />
          </Form.Item>

          <Form.Item
            name="denomination"
            label={t("activerecord.attributes.language.denomination")}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="system" valuePropName="checked">
            <Checkbox>{t("activerecord.attributes.language.system")}</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
